import { useState, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdCloseCircle } from "react-icons/io";
import { PiVan } from "react-icons/pi";
import { IoLocationOutline } from "react-icons/io5";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export const OrderTrack = ({ order }) => {
  let [isOpen, setIsOpen] = useState(false);

  // If no order is passed, try to get from sessionStorage
  const orderData = order || (() => {
    try {
      const checkout = sessionStorage.getItem("checkoutResponse");
      return checkout ? JSON.parse(checkout)?.orders?.[0] : null;
    } catch {
      return null;
    }
  })();

  if (!orderData) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="px-5 py-2 bg-gray-400 text-white rounded font-medium cursor-not-allowed"
        disabled
      >
        Track order
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-[#095228] text-white rounded-lg text-sm font-medium hover:bg-green-800"
      >
        Track order
        <IoLocationOutline />
      </button>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="w-full max-w-[600px] space-y-4 bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div>
              <OrderTracking setIsOpen={setIsOpen} order={orderData} />
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

function OrderTracking({ setIsOpen, order }) {
  const [images, setImages] = useState({}); // map productId -> imageUrl

  // Fetch product images
  useEffect(() => {
    if (!order?.order_items?.length) return;

    const idsToFetch = new Set();

    (order.order_items || []).forEach((it) => {
      if (!it.image && !images[it.product_id]) {
        idsToFetch.add(it.product_id);
      }
    });

    if (idsToFetch.size === 0) return;

    const token =
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("accessToken") ||
      "";

    const fetchForId = async (productId) => {
      try {
        const url = `${API_BASE_URL}/product?id=${encodeURIComponent(productId)}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { token } : {}),
          },
        });
        if (!res.ok) return null;
        const json = await res.json();
        const result = json?.result;
        const img = result?.product_media?.[0]?.url || result?.image || null;
        return img;
      } catch (e) {
        return null;
      }
    };

    let mounted = true;
    (async () => {
      const updates = {};
      for (const id of idsToFetch) {
        if (images[id]) continue;
        const img = await fetchForId(id);
        if (img && mounted) updates[id] = img;
      }
      if (mounted && Object.keys(updates).length) {
        setImages((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [order, images]);

  if (!order) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">No order data available</p>
      </div>
    );
  }

  // Get order date - try multiple possible fields
  const orderDate = 
    order?.created_at
      ? new Date(order.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : order?.order_date
      ? new Date(order.order_date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

  // Estimated delivery: the seller-set date (a plain calendar day stored at midnight UTC) if any,
  // otherwise 30 days from the order date
  const baseDate = order?.created_at || order?.order_date || new Date();
  const deliveryDate = order?.estimate_delivery_date
    ? new Date(order.estimate_delivery_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : new Date(new Date(baseDate).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

  // Get pricing info
  const pricing = order?.pricing || {};
  const currencySymbol = order?.seller?.currency_symbol || "$";
  const subtotal = pricing.subtotal ?? 0;
  const deliveryCharges = pricing.delivery_charges ?? 0;
  const taxAmount = pricing.tax_amount ?? 0;
  const finalAmount = pricing.final_amount ?? subtotal + deliveryCharges + taxAmount;

  // Determine order status steps
  const statusSteps = [
    { name: "Order Placed", status: "placed", apiStatuses: ["new"] },
    { name: "Order Confirmed", status: "confirmed", apiStatuses: ["confirmed", "processing"] },
    { name: "Shipped", status: "shipped", apiStatuses: ["shipped", "transit"] },
    { name: "Out For Delivery", status: "out_for_delivery", apiStatuses: ["out_of_delivery", "out_for_delivery"] },
    { name: "Delivered", status: "delivered", apiStatuses: ["delivered", "completed"] },
  ];
  const isClosed = ["cancelled", "returned"].includes(order?.order_status);
  const history = order?.history || [];
  const stepReachedAt = (step) => {
    const entry = history.filter((h) => step.apiStatuses.includes(h.status)).pop();
    return entry?.created_at
      ? new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : null;
  };
  const closedAt = isClosed
    ? history.filter((h) => h.status === order.order_status).map((h) => h.created_at).pop()
    : null;

  // Helper function to determine if step is active
  const getStepStatus = (stepStatus) => {
    // Order.status values from the API: new, confirmed, processing, shipped, transit, out_of_delivery, delivered, cancelled, returned
    const statusMap = {
      new: "placed",
      pending: "placed",
      confirmed: "confirmed",
      processing: "confirmed",
      shipped: "shipped",
      transit: "shipped",
      out_of_delivery: "out_for_delivery",
      out_for_delivery: "out_for_delivery",
      delivered: "delivered",
      completed: "delivered",
    };

    if (isClosed) return stepReachedAt(statusSteps.find((s) => s.status === stepStatus)) !== null;
    const currentStatus = statusMap[order?.order_status] || "placed";
    const stepIndex = statusSteps.findIndex((s) => s.status === stepStatus);
    const currentIndex = statusSteps.findIndex(
      (s) => s.status === currentStatus
    );

    return stepIndex <= currentIndex;
  };

  // Get shipping address
  const shipping = order?.shipping_address || {};

  return (
    <div className="p-1">
      <div className="flex justify-between items-center">
        <p className="text-[30px] font-bold">Nyawoo</p>
        <button onClick={() => setIsOpen(false)}>
          <IoMdCloseCircle className="text-[30px] text-green-800" />
        </button>
      </div>

      {/* Order Header */}
      <div className="my-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#98911F]">
            Order ID: {order?.order_number || `#${order?.order_id || "N/A"}`}
          </h1>
          {["cancelled", "returned"].includes(order?.order_status) && (
            <span className="px-2 py-1 text-sm my-2 rounded-lg bg-red-100 text-red-700 capitalize">
              {order.order_status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[14px] border-b-2 pb-3">
          <p className="text-gray-600">Order date: <span className="font-semibold">{orderDate}</span></p>
          <p className="border-l-2">
            {["cancelled", "returned"].includes(order?.order_status) ? (
              <span className="flex items-center gap-2 text-red-600 font-medium pl-4 capitalize">
                <PiVan />
                {order.order_status}
              </span>
            ) : order?.order_status === "delivered" ? (
              <span className="flex items-center gap-2 text-green-600 font-medium pl-4">
                <PiVan />
                Delivered
              </span>
            ) : (
              <span className="flex items-center gap-2 text-green-600 font-medium pl-4">
                <PiVan />
                Estimated delivery: <span className="font-semibold">{deliveryDate}</span>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Order Status Progress */}
      <div className="flex items-start mb-6 border-b-2 pb-4">
        {statusSteps.map((step, index) => {
          const reached = getStepStatus(step.status);
          const nextReached = index < statusSteps.length - 1 && getStepStatus(statusSteps[index + 1].status);
          const when = stepReachedAt(step);
          return (
            <div key={step.status} className="relative flex-1 flex flex-col items-center text-center">
              {index < statusSteps.length - 1 && (
                <span
                  className={`absolute top-[7px] left-1/2 w-full h-[2px] ${nextReached ? "bg-green-600" : "bg-gray-300"}`}
                  aria-hidden="true"
                />
              )}
              <span className={`relative z-10 w-4 h-4 rounded-full ${reached ? "bg-green-600" : "bg-gray-300"}`} />
              <p className={`mt-2 text-xs sm:text-sm ${reached ? "text-green-700 font-semibold" : "text-gray-500"}`}>
                {step.name}
              </p>
              {reached && when && <p className="text-[11px] text-gray-500">{when}</p>}
            </div>
          );
        })}
      </div>
      {isClosed && (
        <p className="-mt-3 mb-5 text-sm font-medium text-red-600 capitalize">
          Order {order.order_status}
          {closedAt
            ? ` on ${new Date(closedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`
            : ""}
        </p>
      )}

      {/* Product List */}
      <div className="space-y-4 mb-6 pb-4 border-b-2">
        {(order?.order_items || []).length > 0 ? (
          (order?.order_items || []).map((item) => {
            const src = item.image || images[item.product_id] || "/image/shoeimg.png";
            return (
              <div key={item.id} className="flex items-center gap-3">
                <img
                  src={src}
                  alt={item.product_name || "Product"}
                  className="w-20 h-20 rounded object-contain bg-gray-100"
                />
                <div className="flex-1">
                  <p className="font-semibold">
                    {item.product_name || `Product ${item.product_id}`}
                  </p>
                  <p className="text-gray-500 text-sm">Qty: {item.quantity}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-bold text-green-700">
                    {currencySymbol}{Number(item.total_price || item.unit_price || 0).toFixed(2)}
                  </p>
                  {Number(item.quantity) > 1 && item.unit_price && (
                    <p className="text-xs text-gray-500">{currencySymbol}{Number(item.unit_price).toFixed(2)} each</p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">No items in order</p>
        )}
      </div>

      {/* Payment/Delivery Info */}
      <div className="grid grid-cols-2 gap-6 mb-6 pb-4 border-b-2">
        <div>
          <h2 className="font-semibold mb-2">Payment</h2>
          <p className="text-gray-600">
            {order?.payment_intent_id
              ? Number(order.wallet_amount) > 0 ? "Wallet + Card" : "Card"
              : order?.payment_method
                ? order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)
                : "N/A"}
          </p>
          <p className="text-xs text-gray-500 mt-1 capitalize">
            Status: {order?.payment_status || "pending"}
          </p>
        </div>
        <div>
          <h2 className="font-semibold mb-2">Delivery Address</h2>
          <p className="text-gray-600 text-sm">
            {shipping?.name || "N/A"}
          </p>
          <p className="text-gray-600 text-sm">
            {shipping?.address_line_1 || ""}
            {shipping?.address_line_2 ? `, ${shipping.address_line_2}` : ""}
          </p>
          <p className="text-gray-600 text-sm">
            {shipping?.city || ""} {shipping?.state || ""} {shipping?.postal_code || ""}
          </p>
          {shipping?.country && <p className="text-gray-600 text-sm">{shipping.country}</p>}
          {shipping?.phone && <p className="text-gray-600 text-sm">Phone: {shipping.phone}</p>}
          {shipping?.email && <p className="text-gray-600 text-sm">Email: {shipping.email}</p>}
        </div>
      </div>

      {/* Order Summary */}
      <div>
        <h2 className="font-semibold mb-3">Order Summary</h2>
        <div className="space-y-2 text-gray-700">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{currencySymbol}{Number(subtotal).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery Charges</span>
            <span>{currencySymbol}{Number(deliveryCharges).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax</span>
            <span>{currencySymbol}{Number(taxAmount).toFixed(2)}</span>
          </div>
          {(pricing.tax_breakdown || []).map((rule, index) => (
            <div key={index} className="flex justify-between pl-3 text-xs text-gray-500">
              <span>{rule.name} ({rule.rate}%)</span>
              <span>{currencySymbol}{Number(rule.tax_amount).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold text-lg mt-3 pt-3 border-t-2">
            <span>Total</span>
            <span className="text-green-700">{currencySymbol}{Number(finalAmount).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}